import React, { useRef, useState } from 'react';
import {
  Button,
  Text,
  makeStyles,
  shorthands,
  Link,
} from '@fluentui/react-components';
import {
  ChevronDown24Regular,
  ChevronUp24Regular,
} from '@fluentui/react-icons';
import type { NewsItem } from '@/types';
import { CommentTree } from './CommentTree';
import { AdBanner } from './AdBanner';
import { Header, HeaderRef } from '@/components/Header';


const MAX_TITLE_LEN = 100;

const trimTitle = (t: string) =>
  t.length > MAX_TITLE_LEN ? t.slice(0, MAX_TITLE_LEN) + '…' : t;

const useStyles = makeStyles({
  list: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    columnGap: '12px',
    rowGap: '6px',
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.padding('6px', '8px'),
    borderBottom: '1px solid #eee',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  title: {
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '14px',
    lineHeight: '1.2',
  },
  expand: {
    minWidth: 'auto',
  },
  details: {
    marginTop: '6px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  mediaRow: {
    display: 'flex',
    gap: '8px',
  },
  image: {
    width: '50%',
    height: 'auto',
    borderRadius: '6px',
  },
  iframe: {
    width: '50%',
    aspectRatio: '16 / 9',
    borderRadius: '6px',
    border: 0,
  },
});

export const SimpleNewsList: React.FC<{
  items: NewsItem[];
  routineKey?: string; // "news" for city news, or routine category key
  dateKey?: string;    // REQUIRED for city news or routine category grouping
}> = ({ items, routineKey, dateKey }) => {
  const styles = useStyles();
  const [openId, setOpenId] = useState<string | null>(null);
const headerRef = useRef<HeaderRef>(null);
  // 🔒 Safety guard
  if (routineKey === 'news' && !dateKey) {
    console.error('❌ City news SimpleNewsList missing dateKey');
  }

  return (
     <div>
    {/* 🔹 Show ad once at top if enough items */}
    {items.length > 3 && (
      <div style={{ marginBottom: '16px' }}>
        <AdBanner adSlot="1121804425" />
      </div>
    )}

    <div className={styles.list}>
      {/* 🔹 ROOT CommentTree for routine category with empty items */}
      {routineKey && items.length === 0 && (
        <CommentTree routineKey={routineKey} dateKey={dateKey} />
      )}

      {items.map(item => {
        const isOpen = openId === item.id;
        const watchUrl = item.videoFilePath
          ? `https://www.youtube.com/watch?v=${item.videoFilePath}`
          : null;
        const embedUrl = item.videoFilePath
          ? `https://www.youtube.com/embed/${item.videoFilePath}`
          : null;

        return (
          <div key={item.id} className={styles.row}>
            {/* TITLE ROW */}
            <div className={styles.titleRow}>
              <Text
                className={styles.title}
                title={item.title}
                onClick={() => setOpenId(isOpen ? null : item.id)}
              >
                {trimTitle(item.title)}
              </Text>
              <Button
                appearance="subtle"
                icon={isOpen ? <ChevronUp24Regular /> : <ChevronDown24Regular />}
                className={styles.expand}
                onClick={() => setOpenId(isOpen ? null : item.id)}
              />
            </div>

            {/* EXPANDED CONTENT */}
            {isOpen && (
              <div className={styles.details}>
                {item.description && (
                  <Text size={300}>{item.description}</Text>
                )}

                {(embedUrl || item.imageUrl) && (
                  <div className={styles.mediaRow}>
                    {embedUrl && (
                      <iframe
                        className={styles.iframe}
                        src={embedUrl}
                        title="YouTube video"
                        allowFullScreen
                      />
                    )}
                    {!embedUrl && item.imageUrl && (
                      <img
                        src={item.imageUrl}
                        alt="news"
                        className={styles.image}
                      />
                    )}
                  </div>
                )}

                {/* ✅ Comment wiring */}
                <CommentTree
                  newsId={item.id}     // real news doc id
                  routineKey={routineKey}
                  dateKey={dateKey}    // city-news or routine date group
                   onLoginClick={() => headerRef.current?.openLogin()}
                />

                {watchUrl && (
                  <Link href={watchUrl} target="_blank" rel="noreferrer">
                    Watch on YouTube
                  </Link>
                )}
                {item.url && (
                  <Link href={item.url} target="_blank" rel="noreferrer">
                    Read more
                  </Link>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
    </div>
  );
};
